<template>
  <div class="landing-root">
    <!-- ========== Navbar ========== -->
    <nav class="landing-nav" :class="{ scrolled: navScrolled }">
      <div class="nav-inner">
        <div class="nav-logo" @click="scrollToTop">
          <div class="logo-mark">Z</div>
          <span class="logo-text">智磐 AI</span>
        </div>

        <div class="nav-actions">
          <a class="nav-link" @click.prevent="goServices">企业服务</a>
          <a class="nav-link" @click.prevent="goUsage">使用说明</a>
          <button class="btn-primary" @click="goApp">即刻开始</button>
        </div>

        <button class="mobile-toggle" @click="mobileOpen = !mobileOpen">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      </div>

      <div v-if="mobileOpen" class="mobile-menu">
        <a
          class="mobile-link"
          @click.prevent="
            goServices();
            mobileOpen = false;
          "
          >企业服务</a
        >
        <a
          class="mobile-link"
          @click.prevent="
            goUsage();
            mobileOpen = false;
          "
          >使用说明</a
        >
        <button class="btn-primary mobile-cta" @click="goApp">即刻开始</button>
      </div>
    </nav>

    <!-- ========== Hero ========== -->
    <section class="hero">
      <div class="container hero-center">
        <h1 class="hero-h1">AI 驱动的<br />跨境电商素材工厂</h1>
        <p class="hero-body">
          一张商品图，自动生成主图、场景图、模特图、宣传视频与 Amazon Listing 文案。告别外包与漫长等待，<strong>素材生产提速 10 倍</strong>。
        </p>
        <div class="hero-btns">
          <button class="btn-primary" @click="goApp">即刻开始</button>
        </div>
        <div class="hero-media">
          <video
            ref="heroVideo"
            class="real-video"
            src="/demo.mp4"
            controls
            autoplay
            muted
            loop
            playsinline
            @click="unmuteVideo"
          >
            您的浏览器不支持视频播放。
          </video>
        </div>
      </div>
    </section>

    <!-- ========== Features ========== -->
    <section id="features" class="section">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">核心功能</span>
          <h2 class="section-title">一站式 AI 素材生产流水线</h2>
          <p class="section-desc">从抠图到成片，覆盖跨境电商全链路视觉需求</p>
        </div>
        <div class="features-grid">
          <div class="feature-card" v-for="f in features" :key="f.title">
            <div class="feature-icon-wrap">
              <span class="feature-icon" v-html="f.icon"></span>
            </div>
            <h3 class="feature-title">{{ f.title }}</h3>
            <p class="feature-desc">{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== Why ========== -->
    <section id="why" class="section section-alt">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">竞品对比</span>
          <h2 class="section-title">为何选择智磐 AI</h2>
          <p class="section-desc">专为跨境电商打造，不是通用绘图工具</p>
        </div>
        <div class="compare-table-wrap">
          <table class="compare-table">
            <thead>
              <tr>
                <th>对比维度</th>
                <th class="highlight">智磐 AI</th>
                <th>Midjourney</th>
                <th>Stable Diffusion</th>
                <th>Canva</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in compareRows" :key="row.dim">
                <td class="dim-cell">{{ row.dim }}</td>
                <td class="highlight" v-html="row.us"></td>
                <td>{{ row.mj }}</td>
                <td>{{ row.sd }}</td>
                <td>{{ row.canva }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="why-cards">
          <div class="why-card" v-for="w in whyPoints" :key="w.title">
            <div class="why-num">{{ w.num }}</div>
            <h4 class="why-title">{{ w.title }}</h4>
            <p class="why-desc">{{ w.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== Pricing ========== -->
    <section id="pricing" class="section">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">定价方案</span>
          <h2 class="section-title">简单透明的定价</h2>
          <p class="section-desc">
            按需选择，随时升级。新用户注册即送 5 算力
          </p>
        </div>
        <div class="pricing-grid">
          <div
            class="pricing-card"
            v-for="p in plans"
            :key="p.name"
            :class="{ featured: p.featured }"
          >
            <div v-if="p.featured" class="pricing-badge">最受欢迎</div>
            <h3 class="pricing-name">{{ p.name }}</h3>
            <div class="pricing-price">
              <span class="price-currency">¥</span>
              <span class="price-amount">{{ p.price }}</span>
              <span class="price-period">/ {{ p.period }}</span>
            </div>
            <p class="pricing-note" v-if="p.note">{{ p.note }}</p>
            <ul class="pricing-list">
              <li v-for="item in p.items" :key="item">
                <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ item }}
              </li>
            </ul>
            <button
              :class="['pricing-btn', p.featured ? 'btn-primary' : 'trial-btn']"
              @click="goApp"
            >
              {{ p.cta }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== Footer ========== -->
    <footer class="landing-footer">
      <div class="container footer-inner">
        <div class="footer-left">
          <div class="nav-logo">
            <div class="logo-mark">Z</div>
            <span class="logo-text">智磐 AI</span>
          </div>
          <p class="footer-desc">AI 驱动的跨境电商素材生产平台</p>
        </div>
        <p class="footer-copy">
          &copy; {{ currentYear }} 智磐 AI. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const navScrolled = ref(false);
const mobileOpen = ref(false);
const currentYear = new Date().getFullYear();
const heroVideo = ref(null);

const unmuteVideo = () => {
  if (heroVideo.value) {
    heroVideo.value.muted = false;
  }
};

const onScroll = () => {
  navScrolled.value = window.scrollY > 20;
};
onMounted(() => window.addEventListener("scroll", onScroll));
onUnmounted(() => window.removeEventListener("scroll", onScroll));

const goApp = () => router.push("/upload");
const goServices = () => router.push("/home/services");
const goUsage = () => router.push("/home/usage");

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const features = [
  {
    icon: "&#9986;",
    title: "AI 智能抠图",
    desc: "一键去背景、重绘白底图或场景图，告别 PS 手动抠图，5 秒出片。",
  },
  {
    icon: "&#127912;",
    title: "宣传套图生成",
    desc: "上传一张商品图，AI 自动规划 1+6 组镜头方案，批量生成电商主图与展示图。",
  },
  {
    icon: "&#127916;",
    title: "商品视频生成",
    desc: "图片一键转视频，支持 5~15 秒宣传片，动态展示产品卖点与使用场景。",
  },
  {
    icon: "&#128196;",
    title: "Amazon 文案 & 流量雷达",
    desc: "视觉分析自动撰写 Listing（标题/五点/关键词），同步查询关键词搜索热度。",
  },
];

const compareRows = [
  {
    dim: "电商场景优化",
    us: "<strong>✓ 专属优化</strong>",
    mj: "✗",
    sd: "✗",
    canva: "部分",
  },
  {
    dim: "一键抠图 + 换背景",
    us: "<strong>✓ 内置</strong>",
    mj: "✗",
    sd: "需插件",
    canva: "基础",
  },
  {
    dim: "商品视频生成",
    us: "<strong>✓ 图生视频</strong>",
    mj: "✗",
    sd: "✗",
    canva: "模板拼接",
  },
  {
    dim: "Amazon 文案生成",
    us: "<strong>✓ AI 自动</strong>",
    mj: "✗",
    sd: "✗",
    canva: "✗",
  },
  {
    dim: "关键词热度分析",
    us: "<strong>✓ 内置雷达</strong>",
    mj: "✗",
    sd: "✗",
    canva: "✗",
  },
  {
    dim: "上手难度",
    us: "<strong>零门槛</strong>",
    mj: "需学 Prompt",
    sd: "需部署",
    canva: "低",
  },
  {
    dim: "单张成本",
    us: "<strong>≈ ¥0.3</strong>",
    mj: "≈ ¥1.5",
    sd: "需 GPU",
    canva: "≈ ¥2",
  },
];

const whyPoints = [
  {
    num: "01",
    title: "电商垂直深耕",
    desc: "模型针对白底主图、场景图、模特图做过专项微调，开箱即用。",
  },
  {
    num: "02",
    title: "全链路覆盖",
    desc: "从抠图→套图→视频→文案→关键词，一个平台完成所有素材生产。",
  },
  {
    num: "03",
    title: "成本降低 90%",
    desc: "无需设计师外包，按量付费，单张低至 ¥0.3。",
  },
];

const plans = [
  {
    name: "月度会员",
    price: "9.9",
    period: "月",
    note: "订阅即送 105 算力",
    featured: false,
    cta: "开始试用",
    items: ["105 算力 / 月", "解锁充值功能", "全部 AI 功能", "30~8610 元多档充值"],
  },
  {
    name: "年度会员",
    price: "59.9",
    period: "年",
    note: "限时优惠，省 ¥59",
    featured: true,
    cta: "立即订阅",
    items: [
      "650 算力 / 年",
      "解锁充值功能",
      "全部 AI 功能",
      "30~8610 元多档充值",
    ],
  },
];
</script>

<style scoped>
/* =================== Magi UI Color System Tokens =================== */
.landing-root {
  --bg-main: #faf7f5;
  --bg-nav: rgba(253, 252, 251, 0.84);
  --bg-alt: #ffffff;
  --bg-surface-alt: #f4f4f4;
  --text-primary: #2c2825;
  --text-secondary: #857a70;
  --text-tertiary: #c4bcb5;
  --nav-link: #6e635a;
  --action-bg: #1a1a1a;
  --action-text: #ffffff;
  --action-hover: #333333;
  --sec-border: #d1d1d1;
  --sec-text: #1a1a1a;
  --sec-hover: #efefef;
  --border-light: rgba(44, 40, 37, 0.06);
  --border-medium: #efece8;
  --warm-100: #f7f6f3;
  --warm-200: #efece8;
  --warm-300: #e0dbd5;
  --caramel-400: #eaa572;
  --caramel-500: #df9058;
  --font: "PingFang SC", "Microsoft YaHei", -apple-system, BlinkMacSystemFont,
    sans-serif;
  --max-w: 1280px;
  --px: 2rem;
  --section-py: 6rem;
  --gap: 1.25rem;
  --gap-col: 4rem;
  --pill: 9999px;
  --card-lg: 1.5rem;
  --ui-r: 0.5rem;
  --shadow-media: 0 20px 40px -10px rgba(0, 0, 0, 0.08);
  --shadow-nav: 0 1px 3px rgba(44, 40, 37, 0.06);

  font-family: var(--font);
  color: var(--text-primary);
  background: var(--bg-main);
  background-image: radial-gradient(at 0% 0%, #faf1eb 0px, transparent 50%),
    radial-gradient(at 100% 0%, #fdf7ed 0px, transparent 50%),
    radial-gradient(at 100% 100%, #f2e4da 0px, transparent 50%),
    radial-gradient(at 0% 100%, #fdfcfb 0px, transparent 50%);
  background-attachment: fixed;
  min-height: 100vh;
  overflow-x: hidden;
}

/* =================== Reset scoped =================== */
.landing-root *,
.landing-root *::before,
.landing-root *::after {
  box-sizing: border-box;
}
.landing-root h1,
.landing-root h2,
.landing-root h3,
.landing-root h4,
.landing-root p,
.landing-root ul {
  margin: 0;
  padding: 0;
}
.landing-root ul {
  list-style: none;
}
.landing-root a {
  text-decoration: none;
  color: inherit;
}
.landing-root button {
  border: none;
  cursor: pointer;
  font: inherit;
}
.landing-root input,
.landing-root textarea,
.landing-root select {
  width: auto;
  padding: 0;
  border: none;
  background: transparent;
}

/* =================== Utility =================== */
.container {
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 var(--px);
  width: 100%;
}

/* =================== Buttons =================== */
.btn-primary {
  background: var(--action-bg);
  color: var(--action-text);
  border-radius: var(--pill);
  padding: 0.75rem 1.75rem;
  font-weight: 500;
  font-size: 0.95rem;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px -4px rgba(234, 165, 114, 0.3);
}
.btn-primary:hover {
  background: var(--action-hover);
  transform: translateY(-1px);
  box-shadow: 0 12px 20px -4px rgba(234, 165, 114, 0.4);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--sec-border);
  color: var(--sec-text);
  border-radius: var(--pill);
  padding: 0.75rem 1.75rem;
  font-weight: 500;
  font-size: 0.95rem;
  transition: background 0.25s, transform 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn-secondary:hover {
  background: var(--sec-hover);
  transform: translateY(-1px);
}

/* =================== Navbar =================== */
.landing-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--bg-nav);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: box-shadow 0.3s;
}
.landing-nav.scrolled {
  box-shadow: var(--shadow-nav);
}

.nav-inner {
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 0 var(--px);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.logo-mark {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--action-bg);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
}
.logo-text {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--text-primary);
}

.nav-link {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--nav-link);
  cursor: pointer;
  transition: color 0.2s;
}
.nav-link:hover {
  color: var(--text-primary);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}
.mobile-toggle {
  display: none;
  background: none;
  padding: 4px;
  color: var(--text-primary);
}

.mobile-menu {
  display: none;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem var(--px) 1.5rem;
  border-top: 1px solid var(--border-light);
}
.mobile-link {
  font-size: 1rem;
  padding: 0.6rem 0;
  color: var(--nav-link);
  cursor: pointer;
}
.mobile-cta {
  width: 100%;
  margin-top: 0.5rem;
}

/* =================== Hero (centered, video below) =================== */
.hero {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  padding-top: 180px;
  padding-bottom: 2rem;
}
.hero-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.25rem;
}
.hero-h1 {
  font-size: 3.5rem;
  font-weight: 600;
  line-height: 1.62;
  letter-spacing: 0.05em;
  margin-bottom: 0;
  word-break: keep-all;
}
.hero-body {
  font-size: 1.125rem;
  font-weight: 400;
  line-height: 2.02;
  color: var(--text-secondary);
  margin-bottom: 0;
  max-width: 760px;
  word-break: keep-all;
}
.hero-body strong {
  color: var(--text-primary);
}
.hero-btns {
  display: flex;
  gap: var(--gap);
  justify-content: center;
  margin-top: 2rem;
  margin-bottom: 4.2rem;
}

.hero-btns .btn-primary {
  min-width: 300px;
  padding: 1.05rem 4.2rem;
  font-size: 1.02rem;
}

.hero-media {
  width: 100%;
  max-width: 800px;
}
.real-video {
  width: 100%;
  border-radius: 1rem;
  display: block;
}

/* =================== Section Shared =================== */
.section {
  padding: var(--section-py) 0;
}
.section-alt {
  background: transparent;
}

.section-header {
  text-align: center;
  margin-bottom: 3.5rem;
}
.section-badge {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--caramel-500);
  background: rgba(234, 165, 114, 0.1);
  padding: 0.3rem 0.9rem;
  border-radius: var(--pill);
  margin-bottom: 1rem;
  letter-spacing: 0.04em;
}
.section-title {
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 0.75rem;
  border-left: none;
  padding-left: 0;
  word-break: keep-all;
}
.section-desc {
  font-size: 1.05rem;
  color: var(--text-secondary);
  line-height: 1.78;
  word-break: keep-all;
}

/* =================== Features =================== */
.features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}
.feature-card {
  background: var(--bg-alt);
  border: 1px solid var(--border-light);
  border-radius: var(--card-lg);
  padding: 2rem 1.5rem;
  transition: transform 0.3s, box-shadow 0.3s;
}
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(44, 40, 37, 0.06);
}
.feature-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--warm-200);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}
.feature-icon {
  font-size: 22px;
  line-height: 1;
}
.feature-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.feature-desc {
  font-size: 0.9rem;
  line-height: 1.78;
  color: var(--text-secondary);
  word-break: keep-all;
}

/* =================== Compare Table =================== */
.compare-table-wrap {
  overflow-x: auto;
  margin-bottom: 3rem;
  border: 1px solid var(--border-light);
  border-radius: var(--card-lg);
  background: rgba(250, 247, 245, 0.72);
}
.compare-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 640px;
}
.compare-table th,
.compare-table td {
  padding: 0.85rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border-light);
  line-height: 1.72;
}
.compare-table thead th {
  background: var(--warm-200);
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-secondary);
  letter-spacing: 0.02em;
}
.compare-table thead th.highlight {
  color: var(--text-primary);
}
.compare-table td.highlight {
  background: rgba(234, 165, 114, 0.04);
  font-weight: 500;
}
.compare-table td.dim-cell {
  text-align: left;
  font-weight: 500;
  color: var(--text-primary);
}
.compare-table tbody tr:last-child td {
  border-bottom: none;
}

/* =================== Why Points =================== */
.why-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.why-card {
  background: var(--warm-100);
  border: 1px solid var(--border-light);
  border-radius: var(--card-lg);
  padding: 2rem 1.5rem;
  transition: transform 0.3s, box-shadow 0.3s;
}
.why-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 28px rgba(44, 40, 37, 0.06);
}
.why-num {
  font-size: 2rem;
  font-weight: 700;
  color: var(--warm-300);
  margin-bottom: 0.75rem;
  line-height: 1;
}
.why-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.why-desc {
  font-size: 0.88rem;
  line-height: 1.8;
  color: var(--text-secondary);
}

/* =================== Pricing =================== */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 780px;
  margin: 0 auto;
}
.pricing-card {
  background: var(--bg-alt);
  border: 1px solid var(--border-light);
  border-radius: var(--card-lg);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;
}
.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(44, 40, 37, 0.07);
}
.pricing-card.featured {
  border-color: var(--action-bg);
  box-shadow: 0 8px 28px rgba(44, 40, 37, 0.08);
}
.pricing-badge {
  position: absolute;
  top: -12px;
  background: var(--action-bg);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 1rem;
  border-radius: var(--pill);
}
.pricing-name {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
.pricing-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 0.5rem;
}
.price-currency {
  font-size: 1.25rem;
  font-weight: 600;
}
.price-amount {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1;
}
.price-period {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-left: 2px;
}
.pricing-note {
  font-size: 0.8rem;
  color: var(--caramel-500);
  font-weight: 500;
  margin-bottom: 0.5rem;
}
.pricing-list {
  width: 100%;
  margin: 1.5rem 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.pricing-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.78;
}
.check-icon {
  width: 18px;
  height: 18px;
  color: var(--caramel-400);
  flex-shrink: 0;
}
.pricing-btn {
  width: 100%;
  padding: 0.85rem;
}

/* =================== Footer =================== */
.landing-footer {
  border-top: 1px solid var(--border-light);
  padding: 2rem 0;
}
.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}
.footer-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.footer-desc {
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.75;
}
.footer-copy {
  font-size: 0.8rem;
  color: var(--text-tertiary);
}

/* =================== Responsive =================== */
@media (max-width: 1024px) {
  .hero {
    padding-top: 132px;
  }
  .hero-h1 {
    font-size: 2.5rem;
  }
  .hero-center {
    gap: 1rem;
  }
  .hero-body {
    line-height: 1.9;
    max-width: 720px;
  }
  .hero-btns {
    margin-top: 1.5rem;
    margin-bottom: 3.4rem;
  }
  .hero-btns .btn-primary {
    min-width: 260px;
    padding: 1rem 3.4rem;
  }
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .why-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 768px) {
  .hero {
    padding-top: 96px;
  }
  .nav-actions {
    display: none;
  }
  .mobile-toggle {
    display: block;
  }
  .mobile-menu {
    display: flex;
  }
  .hero-center {
    gap: 0.9rem;
    padding: 0 1rem;
  }
  .hero-h1 {
    font-size: 1.75rem;
    line-height: 1.5;
    letter-spacing: 0.02em;
  }
  .hero-body {
    font-size: 0.95rem;
    line-height: 1.85;
    max-width: 100%;
    padding: 0 0.5rem;
  }
  .hero-btns {
    margin-top: 1.2rem;
    margin-bottom: 2.8rem;
  }
  .hero-btns .btn-primary {
    min-width: 200px;
    padding: 0.95rem 2.8rem;
  }
  .hero-media {
    max-width: 100%;
  }
  .section-header {
    padding: 0 0.5rem;
  }
  .section-title {
    font-size: 1.5rem;
    line-height: 1.45;
  }
  .section-desc {
    font-size: 0.92rem;
  }
  .features-grid {
    grid-template-columns: 1fr;
  }
  .feature-card {
    text-align: center;
  }
  .feature-icon-wrap {
    margin: 0 auto 1rem;
  }
  .why-cards {
    grid-template-columns: 1fr;
  }
  .pricing-grid {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
  .section {
    padding: 3.5rem 0;
  }
  .footer-inner {
    flex-direction: column;
    text-align: center;
  }
}
</style>
